// @ts-nocheck
/* eslint-disable react/prop-types */

import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { flexRender } from '@tanstack/react-table';
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  TableSortLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useVirtualizer } from '@tanstack/react-virtual';

import { ReactComponent as DownwardArrow } from '../../../assets/DownwardArrow.svg';
import { ReactComponent as UpwardIcon } from '../../../assets/UpwardArrow.svg';

import SolyticsBox from '../../SolyticsBox.jsx';
import SolyticsCheckbox from '../../SolyticsCheckbox.jsx';
import SolyticsNoDataPresent from '../../SolyticsNoDataPresent.jsx';

const ROW_HEIGHT = 55;
const HEADER_HEIGHT = 44;
const DEFAULT_COLUMN_WIDTH = 150;
const DEFAULT_DEBOUNCE_TIME_MS = 100;

// The VirtualRow styled component targets this class inside its hover/selected
const STICKY_CELL_CLASS = 'VirtualTable-stickyCell';

// VirtualRow — the only styled component needed.

const VirtualRow = styled(SolyticsBox, {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' && prop !== 'enableRowSelection',
})(({ theme, isSelected, enableRowSelection }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  height: `${ROW_HEIGHT}px`,
  cursor: enableRowSelection ? 'pointer' : 'default',
  borderBottom: `1px solid ${theme.palette.grey?.[60]}`,
  backgroundColor: isSelected
    ? theme.palette.selection?.main || theme.palette.action.selected
    : 'transparent',
  '&:hover': {
    backgroundColor: isSelected
      ? theme.palette.selection?.main || theme.palette.action.selected
      : theme.palette.action.hover,
    [`& .${STICKY_CELL_CLASS}`]: {
      background: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover}), ${theme.palette.background.paper}`,
    },
  },

  // Selected state sticky cell override
  [`& .${STICKY_CELL_CLASS}`]: {
    backgroundColor: theme.palette.background.paper,
    ...(isSelected && {
      backgroundColor:
        theme.palette.selection?.main || theme.palette.action.selected,
    }),
  },

  // Selected + hover
  ...(isSelected && {
    '&:hover': {
      backgroundColor:
        theme.palette.selection?.main || theme.palette.action.selected,
      [`& .${STICKY_CELL_CLASS}`]: {
        backgroundColor:
          theme.palette.selection?.main || theme.palette.action.selected,
      },
    },
  }),

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
  },
}));

/**
 * SolyticsTableVirtualized - Virtualized table renderer using @tanstack/react-virtual
 *
 * @param {Object}   props.table                   - TanStack table instance
 * @param {string}   props.size                     - 'small' | 'medium'
 * @param {string}   props.ariaLabel                - Table aria-label
 * @param {boolean}  props.enableRowSelection       - Enable row selection
 * @param {Array}    props.currentSelectedRows      - Currently selected row IDs
 * @param {Function} props.handleRowSelectionChange - Row selection change handler
 * @param {Function} props.handleSelectAll          - Select all rows handler
 * @param {boolean}  props.allRowsSelected          - All rows selected flag
 * @param {boolean}  props.someRowsSelected         - Some rows selected flag
 * @param {boolean}  props.isLoading                - Loading state
 * @param {boolean}  props.hasData                  - Whether table has data
 * @param {Array}    props.visibleColumns            - Visible columns array
 * @param {Array}    props.rows                     - Table rows
 * @param {Function} props.renderEmpty              - Empty state renderer (function)
 * @param {Function} props.getCellProps             - Cell props getter
 * @param {boolean}  props.enableColumnResizing     - Enable column resizing
 */
const SolyticsTableVirtualized = ({
  table,
  size = 'medium',
  ariaLabel,
  enableRowSelection = false,
  currentSelectedRows = [],
  handleRowSelectionChange,
  handleSelectAll,
  allRowsSelected = false,
  someRowsSelected = false,
  isLoading = false,
  hasData = false,
  visibleColumns = [],
  rows = [],
  renderEmpty,
  getCellProps,
  enableColumnResizing = true,
  variant = 'list', // 'list' (default) or 'grid'
}) => {
  // Show message if no columns are visible
  if (
    !visibleColumns ||
    visibleColumns.length === 0 ||
    (visibleColumns.length === 1 && visibleColumns[0].id === 'rowActions') //for contextMenu only column
  ) {
    return (
      <SolyticsBox
        sx={{
          width: '100%',
          height: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SolyticsNoDataPresent message="No columns are visible. Please configure columns in Table Settings." />
      </SolyticsBox>
    );
  }

  const outerContainerRef = useRef(null);
  const parentRef = useRef(null);
  const headerScrollRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Observe outer container for width — not parentRef which changes on scroll
  useEffect(() => {
    const el = outerContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Checkbox column width
  const checkboxWidth = enableRowSelection ? 56 : 0;

  // Current live column sizing state from TanStack
  const columnSizing = table.getState().columnSizing;

  const columnSizesRef = useRef({});
  const columnVirtualizerRef = useRef(null);

  const leftPinnedColumns = useMemo(
    () => visibleColumns.filter((col) => col.getIsPinned() === 'left'),
    [visibleColumns]
  );

  const rightPinnedColumns = useMemo(
    () => visibleColumns.filter((col) => col.getIsPinned() === 'right'),
    [visibleColumns]
  );

  const centerColumns = useMemo(
    () => visibleColumns.filter((col) => !col.getIsPinned()),
    [visibleColumns]
  );

  const isGridVariant = useMemo(() => variant === 'grid', [variant]);

  const leftPinnedWidth = useMemo(
    () =>
      leftPinnedColumns.reduce(
        (sum, col) => sum + Math.round(col.getSize()),
        0
      ),
    [leftPinnedColumns]
  );

  const rightPinnedWidth = useMemo(
    () =>
      rightPinnedColumns.reduce(
        (sum, col) => sum + Math.round(col.getSize()),
        0
      ),
    [rightPinnedColumns]
  );

  const centerBaseWidth = useMemo(
    () => centerColumns.reduce((sum, col) => sum + col.getSize(), 0),
    [centerColumns]
  );

  const centerViewportWidth = useMemo(
    () =>
      Math.max(
        containerWidth - checkboxWidth - leftPinnedWidth - rightPinnedWidth,
        0
      ),
    [containerWidth, checkboxWidth, leftPinnedWidth, rightPinnedWidth]
  );

  const centerScaleFactor = useMemo(() => {
    if (
      centerViewportWidth > 0 &&
      centerBaseWidth > 0 &&
      centerBaseWidth < centerViewportWidth
    ) {
      return centerViewportWidth / centerBaseWidth;
    }
    return 1;
  }, [centerViewportWidth, centerBaseWidth]);

  useEffect(() => {
    // Debounce update to avoid excessive rerenders
    let timeoutId = null;
    timeoutId = setTimeout(() => {
      centerColumns.forEach((col, index) => {
        columnSizesRef.current[index] = Math.round(
          col.getSize() * centerScaleFactor
        );
      });
      columnVirtualizerRef.current?.measure();
    }, DEFAULT_DEBOUNCE_TIME_MS);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [centerScaleFactor, columnSizing, centerColumns]);

  const leftPinnedOffsets = useMemo(() => {
    const offsets = {};
    let offset = checkboxWidth;
    leftPinnedColumns.forEach((col) => {
      offsets[col.id] = offset;
      offset += Math.round(col.getSize());
    });
    return offsets;
  }, [checkboxWidth, leftPinnedColumns]);

  const rightPinnedOffsets = useMemo(() => {
    const offsets = {};
    let offset = 0;
    for (let i = rightPinnedColumns.length - 1; i >= 0; i--) {
      const col = rightPinnedColumns[i];
      offsets[col.id] = offset;
      offset += Math.round(col.getSize());
    }
    return offsets;
  }, [rightPinnedColumns]);

  // Row virtualizer
  const rowVirtualizer = useVirtualizer({
    count: hasData && !isLoading ? rows.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 15,
  });

  // Column virtualizer
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: centerColumns.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(
      (index) => columnSizesRef.current[index] ?? DEFAULT_COLUMN_WIDTH,
      []
    ),
    overscan: 5,
  });

  useEffect(() => {
    columnVirtualizerRef.current = columnVirtualizer;
  }, [columnVirtualizer]);

  // Sync horizontal scroll between header and body
  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl || !headerScrollRef.current) return;

    const handleScroll = () => {
      if (headerScrollRef.current)
        headerScrollRef.current.scrollLeft = scrollEl.scrollLeft;
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, []);

  // Render sort direction icon
  const renderSortIcon = useCallback((column) => {
    if (!column.getCanSort()) return null;
    const dir = column.getIsSorted();
    if (dir === 'asc')
      return <UpwardIcon style={{ width: '16px', height: '16px' }} />;
    if (dir === 'desc')
      return <DownwardArrow style={{ width: '16px', height: '16px' }} />;
    return <span />;
  }, []);

  // Common header cell typography styles
  const headerTypographySx = useMemo(
    () => ({
      color: (theme) => theme.palette.text.primary,
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.03px',
      fontFamily: (theme) => theme.typography.fontFamily,
    }),
    []
  );

  const bodyTypographySx = useMemo(
    () => ({
      color: (theme) => theme.palette.text.primary,
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.035px',
      fontFamily: (theme) => theme.typography.fontFamily,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      boxSizing: 'border-box',
      '& .MuiTypography-root': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    }),
    []
  );

  const resizeHandleSx = useCallback(
    (header) => ({
      position: 'absolute',
      right: 0,
      top: 0,
      height: '100%',
      width: '48px', //selectable width
      cursor: 'col-resize',
      userSelect: 'none',
      touchAction: 'none',
      backgroundColor: 'transparent',
      zIndex: 1,
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        top: '10%',
        height: '80%',
        width: '3px', //separator width
        borderRadius: '2px',
        backgroundColor: 'transparent',
        transition: 'background-color 200ms ease',
      },
      '&:hover::after': {
        backgroundColor: (theme) => theme.palette.grey[70],
      },
      ...(header.column.getIsResizing() && {
        '&::after': {
          content: '""',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          top: '10%',
          height: '80%',
          width: '3px', //separator width
          borderRadius: '2px',
          backgroundColor: (theme) => theme.palette.grey[70],
          transition: 'background-color 200ms ease',
        },
      }),
    }),
    []
  );

  const headerByColumnId = useMemo(() => {
    const map = new Map();
    table.getHeaderGroups().forEach((group) => {
      group.headers.forEach((header) => {
        if (!header.isPlaceholder) map.set(header.column.id, header);
      });
    });
    return map;
  }, [table]);

  const centerVirtualColumns = columnVirtualizer.getVirtualItems();
  const totalCenterWidth = columnVirtualizer.getTotalSize();
  const centerScrollableWidth = Math.max(totalCenterWidth, centerViewportWidth);
  const totalRowWidth = Math.max(
    checkboxWidth + leftPinnedWidth + centerScrollableWidth + rightPinnedWidth,
    containerWidth
  );

  const renderHeaderContent = useCallback(
    (header, column) => {
      if (column.getCanSort()) {
        return (
          <TableSortLabel
            active={!!column.getIsSorted()}
            direction={column.getIsSorted() || 'asc'}
            onClick={column.getToggleSortingHandler()}
            IconComponent={() => renderSortIcon(column)}
            aria-label={`Sort by ${column.columnDef.header || column.id}`}
            aria-sort={
              column.getIsSorted() === 'asc'
                ? 'ascending'
                : column.getIsSorted() === 'desc'
                  ? 'descending'
                  : 'none'
            }
            sx={{
              ...headerTypographySx,
              '& .MuiTableSortLabel-icon': {
                fontSize: '16px !important',
                color: (theme) => theme.palette.text.primary,
                marginLeft: '4px',
              },
              '&:hover': {
                color: (theme) => theme.palette.text.primary,
              },
            }}
          >
            {header?.isPlaceholder
              ? null
              : flexRender(
                  column.columnDef.header,
                  header && typeof header.getContext === 'function'
                    ? header.getContext()
                    : undefined
                )}
          </TableSortLabel>
        );
      }

      return (
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            ...headerTypographySx,
          }}
        >
          {header?.isPlaceholder
            ? null
            : flexRender(
                column.columnDef.header,
                header && typeof header.getContext === 'function'
                  ? header.getContext()
                  : undefined
              )}
        </SolyticsBox>
      );
    },
    [headerTypographySx, renderSortIcon]
  );

  // Loading skeleton for virtualized mode
  if (isLoading) {
    return (
      <SolyticsBox
        sx={{
          height: '100%',
          overflow: 'hidden',
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        {/* Skeleton header */}
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: HEADER_HEIGHT,
            borderBottom: (theme) => `1px solid ${theme.palette.grey?.[60]}`,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          {enableRowSelection && (
            <SolyticsBox sx={{ width: 56, px: 2 }}>
              <Skeleton variant="rectangular" width={24} height={24} />
            </SolyticsBox>
          )}
          {visibleColumns.slice(0, 10).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <SolyticsBox key={i} sx={{ flex: 1, px: 2 }}>
              <Skeleton variant="text" width="70%" />
            </SolyticsBox>
          ))}
        </SolyticsBox>
        {/* Skeleton rows */}
        {Array.from({ length: 5 }).map((_, index) => (
          <SolyticsBox
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: ROW_HEIGHT,
              borderBottom: (theme) => `1px solid ${theme.palette.grey?.[60]}`,
            }}
          >
            {enableRowSelection && (
              <SolyticsBox sx={{ width: 56, px: 2 }}>
                <Skeleton variant="rectangular" width={24} height={24} />
              </SolyticsBox>
            )}
            {visibleColumns.slice(0, 10).map((i) => (
              <SolyticsBox key={i} sx={{ flex: 1, px: 2 }}>
                <Skeleton variant="text" />
              </SolyticsBox>
            ))}
          </SolyticsBox>
        ))}
      </SolyticsBox>
    );
  }

  // Empty state
  if (!hasData) {
    return (
      <SolyticsBox
        sx={{
          height: '100%',
          overflow: 'hidden',
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Table size={size} aria-label={ariaLabel || 'Data table'}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      ...headerTypographySx,
                      height: HEADER_HEIGHT,
                      padding: '12px 16px',
                      borderBottom: (theme) =>
                        `1px solid ${theme.palette.grey?.[60]}`,
                      backgroundColor: (theme) =>
                        theme.palette.background.paper,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
        </Table>
        {typeof renderEmpty === 'function' ? renderEmpty() : renderEmpty}
      </SolyticsBox>
    );
  }

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRowSize = rowVirtualizer.getTotalSize();

  return (
    <SolyticsBox
      ref={outerContainerRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      {/* Sticky Header */}
      <SolyticsBox
        ref={headerScrollRef}
        sx={{
          flexShrink: 0,
          overflow: 'hidden',
          backgroundColor: (theme) => theme.palette.background.paper,
          borderBottom: (theme) => `1px solid ${theme.palette.grey?.[60]}`,
        }}
      >
        <SolyticsBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: `${HEADER_HEIGHT}px`,
            width: totalRowWidth,
            position: 'relative',
          }}
        >
          {/* Select all checkbox header */}
          {enableRowSelection && (
            <SolyticsBox
              onClick={() => handleSelectAll(!allRowsSelected)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectAll(!allRowsSelected);
                }
              }}
              tabIndex={0}
              role="checkbox"
              aria-label="Select all rows"
              aria-controls="table-body"
              sx={{
                width: checkboxWidth,
                minWidth: checkboxWidth,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 16px',
                backgroundColor: (theme) => theme.palette.background.paper,
                position: 'sticky',
                left: 0,
                zIndex: (theme) => theme.zIndex.appBar + 4,
              }}
            >
              <SolyticsCheckbox
                name="select-all-rows"
                indeterminate={someRowsSelected}
                checked={allRowsSelected}
              />
            </SolyticsBox>
          )}

          {/* Left pinned headers */}
          {leftPinnedColumns.map((column) => {
            const header = headerByColumnId.get(column.id);
            const width = Math.round(column.getSize());
            const isLastLeftPinned = column.getIsLastColumn?.('left');
            return (
              <SolyticsBox
                key={`left-header-${column.id}`}
                sx={{
                  position: 'sticky',
                  left: `${leftPinnedOffsets[column.id] || checkboxWidth}px`,
                  width: `${width}px`,
                  minWidth: `${width}px`,
                  maxWidth: `${width}px`,
                  height: `${HEADER_HEIGHT}px`,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderRight: !(isLastLeftPinned || isGridVariant)
                    ? 'none'
                    : (theme) => `1px solid ${theme.palette.grey?.[60]}`,
                  zIndex: (theme) => theme.zIndex.appBar + 3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {renderHeaderContent(header, column)}
                {enableColumnResizing && header?.column?.getCanResize?.() && (
                  <SolyticsBox
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onDoubleClick={() => header.column.resetSize()}
                    role="separator"
                    aria-orientation="vertical"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') header.column.resetSize();
                    }}
                    sx={resizeHandleSx(header)}
                  />
                )}
              </SolyticsBox>
            );
          })}

          {/* Virtualized center headers */}
          <SolyticsBox
            sx={{
              position: 'relative',
              height: `${HEADER_HEIGHT}px`,
              width: `${centerScrollableWidth}px`,
            }}
          >
            {centerVirtualColumns.map((virtualColumn) => {
              const column = centerColumns[virtualColumn.index];
              const header = headerByColumnId.get(column?.id);
              if (!column) return null;

              return (
                <SolyticsBox
                  key={`center-header-${column.id}`}
                  sx={{
                    position: 'absolute',
                    left: `${virtualColumn.start}px`,
                    width: `${virtualColumn.size}px`,
                    height: `${HEADER_HEIGHT}px`,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    backgroundColor: (theme) => theme.palette.background.paper,
                    borderRight: !isGridVariant
                      ? 'none'
                      : (theme) => `1px solid ${theme.palette.grey?.[60]}`,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {renderHeaderContent(header, column)}

                  {/* Column resize handle */}
                  {enableColumnResizing && header?.column?.getCanResize?.() && (
                    <SolyticsBox
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      onDoubleClick={() => header.column.resetSize()}
                      role="separator"
                      aria-orientation="vertical"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') header.column.resetSize();
                      }}
                      sx={resizeHandleSx(header)}
                    />
                  )}
                </SolyticsBox>
              );
            })}
          </SolyticsBox>

          {/* Right pinned headers */}
          {rightPinnedColumns.map((column) => {
            const header = headerByColumnId.get(column.id);
            const width = Math.round(column.getSize());
            const isFirstRightPinned = column.getIsFirstColumn?.('right');

            return (
              <SolyticsBox
                key={`right-header-${column.id}`}
                sx={{
                  position: 'sticky',
                  right: `${rightPinnedOffsets[column.id] || 0}px`,
                  width: `${width}px`,
                  minWidth: `${width}px`,
                  maxWidth: `${width}px`,
                  height: `${HEADER_HEIGHT}px`,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: (theme) => theme.palette.background.paper,
                  borderLeft: !(isFirstRightPinned || isGridVariant)
                    ? 'none'
                    : (theme) => `1px solid ${theme.palette.grey?.[60]}`,
                  zIndex: (theme) => theme.zIndex.appBar + 3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {renderHeaderContent(header, column)}
                {enableColumnResizing && header?.column?.getCanResize?.() && (
                  <SolyticsBox
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onDoubleClick={() => header.column.resetSize()}
                    role="separator"
                    aria-orientation="vertical"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') header.column.resetSize();
                    }}
                    sx={resizeHandleSx(header)}
                  />
                )}
              </SolyticsBox>
            );
          })}
        </SolyticsBox>
      </SolyticsBox>

      {/* Virtualized body */}
      <SolyticsBox
        ref={parentRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          '::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <SolyticsBox
          sx={{
            height: `${totalRowSize}px`,
            width: totalRowWidth,
            position: 'relative',
          }}
        >
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            if (!row) return null;

            const isSelected = currentSelectedRows.includes(row.id);
            const cellByColumnId = new Map(
              row.getVisibleCells().map((cell) => [cell.column.id, cell])
            );

            return (
              <VirtualRow
                key={row.id}
                isSelected={isSelected}
                enableRowSelection={enableRowSelection}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  width: totalRowWidth,
                }}
                onClick={() => {
                  if (enableRowSelection)
                    handleRowSelectionChange(row.id, !isSelected);
                }}
                onKeyDown={(e) => {
                  if (
                    enableRowSelection &&
                    (e.key === 'Enter' || e.key === ' ')
                  ) {
                    e.preventDefault();
                    handleRowSelectionChange(row.id, !isSelected);
                  }
                }}
                tabIndex={enableRowSelection ? 0 : -1}
                role={enableRowSelection ? 'row' : undefined}
                aria-selected={enableRowSelection ? isSelected : undefined}
              >
                {/* Sticky checkbox — STICKY_CELL_CLASS makes it a target for
                    VirtualRow's hover descendant selector */}
                {enableRowSelection && (
                  <SolyticsBox
                    className={STICKY_CELL_CLASS}
                    aria-label={`Select row ${row.id}`}
                    sx={{
                      width: checkboxWidth,
                      minWidth: checkboxWidth,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px',
                      position: 'sticky',
                      left: 0,
                      zIndex: (theme) => theme.zIndex.appBar + 2,
                      // Default opaque base — VirtualRow's hover rule overrides this
                      backgroundColor: (theme) =>
                        theme.palette.background.paper,
                    }}
                  >
                    <SolyticsCheckbox
                      name={`select-row-${row.id}`}
                      checked={isSelected}
                    />
                  </SolyticsBox>
                )}

                {/* Left pinned cells — STICKY_CELL_CLASS */}
                {leftPinnedColumns.map((column) => {
                  const cell = cellByColumnId.get(column.id);
                  if (!cell) return null;
                  const width = Math.round(column.getSize());
                  const isLastLeftPinned = column.getIsLastColumn?.('left');
                  return (
                    <SolyticsBox
                      key={`left-cell-${cell.id}`}
                      className={STICKY_CELL_CLASS}
                      title={
                        typeof cell.getValue() === 'string' ||
                        typeof cell.getValue() === 'number'
                          ? String(cell.getValue())
                          : undefined
                      }
                      sx={{
                        position: 'sticky',
                        left: `${leftPinnedOffsets[column.id] || checkboxWidth}px`,
                        width: `${width}px`,
                        minWidth: `${width}px`,
                        maxWidth: `${width}px`,
                        height: `${ROW_HEIGHT}px`,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                        borderRight: !(isLastLeftPinned || isGridVariant)
                          ? 'none'
                          : (theme) => `1px solid ${theme.palette.grey?.[60]}`,
                        zIndex: (theme) => theme.zIndex.appBar + 1,
                        ...bodyTypographySx,
                      }}
                      {...(typeof getCellProps === 'function'
                        ? getCellProps(cell, row)
                        : {})}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </SolyticsBox>
                  );
                })}

                {/* Virtualized center data cells */}
                <SolyticsBox
                  sx={{
                    position: 'relative',
                    height: `${ROW_HEIGHT}px`,
                    width: `${centerScrollableWidth}px`,
                  }}
                >
                  {centerVirtualColumns.map((virtualColumn) => {
                    const column = centerColumns[virtualColumn.index];
                    const cell = column ? cellByColumnId.get(column.id) : null;
                    if (!cell) return null;

                    return (
                      <SolyticsBox
                        key={`center-cell-${cell.id}`}
                        title={
                          typeof cell.getValue() === 'string' ||
                          typeof cell.getValue() === 'number'
                            ? String(cell.getValue())
                            : undefined
                        }
                        sx={{
                          position: 'absolute',
                          left: `${virtualColumn.start}px`,
                          width: `${virtualColumn.size}px`,
                          height: `${ROW_HEIGHT}px`,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '16px',
                          borderRight: !isGridVariant
                            ? 'none'
                            : (theme) =>
                                `1px solid ${theme.palette.grey?.[60]}`,
                          ...bodyTypographySx,
                        }}
                        {...(typeof getCellProps === 'function'
                          ? getCellProps(cell, row)
                          : {})}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SolyticsBox>
                    );
                  })}
                </SolyticsBox>

                {/* Right pinned cells — STICKY_CELL_CLASS */}
                {rightPinnedColumns.map((column) => {
                  const cell = cellByColumnId.get(column.id);
                  if (!cell) return null;
                  const width = Math.round(column.getSize());
                  const isFirstRightPinned = column.getIsFirstColumn?.('right');

                  return (
                    <SolyticsBox
                      key={`right-cell-${cell.id}`}
                      className={STICKY_CELL_CLASS}
                      title={
                        typeof cell.getValue() === 'string' ||
                        typeof cell.getValue() === 'number'
                          ? String(cell.getValue())
                          : undefined
                      }
                      sx={{
                        position: 'sticky',
                        right: `${rightPinnedOffsets[column.id] || 0}px`,
                        width: `${width}px`,
                        minWidth: `${width}px`,
                        maxWidth: `${width}px`,
                        height: `${ROW_HEIGHT}px`,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                        borderLeft: !(isFirstRightPinned || isGridVariant)
                          ? 'none'
                          : (theme) => `1px solid ${theme.palette.grey?.[60]}`,
                        zIndex: (theme) => theme.zIndex.appBar + 1,
                        ...bodyTypographySx,
                      }}
                      {...(typeof getCellProps === 'function'
                        ? getCellProps(cell, row)
                        : {})}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </SolyticsBox>
                  );
                })}
              </VirtualRow>
            );
          })}
        </SolyticsBox>
      </SolyticsBox>
    </SolyticsBox>
  );
};

export default SolyticsTableVirtualized;
